import React, { useEffect, useState } from "react";
import {
  fetchMembershipPlans,
  createMembershipPlan,
  updateMembershipPlan,
  deleteMembershipPlan,
  fetchServices,
  getCurrency,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Membership plans catalog (SM2b). A plan is a BUNDLE of catalog services: its
// monthly price is auto-summed from the included services' public prices, less a
// bundle discount. Registration (MembersPage) snapshots the plan's price/areas
// onto the member, so this page is the single place a plan is authored.
const S = {
  en: {
    title: "Service Bundles",
    intro: "Bundle catalog services into a plan. The monthly price is summed from the services' public prices, minus a bundle discount.",
    new: "New plan", name: "Plan name", services: "Included services",
    no_services: "No active services yet — add services first in the catalog.",
    discount: "Bundle discount %", cycle: "Billing cycle",
    monthly: "Monthly", quarterly: "Quarterly", annual: "Annual",
    active: "Active", save: "Save plan", saved: "Plan saved.",
    subtotal: "Subtotal", price: "Monthly price", none: "No plans yet — add your first above.",
    confirm_delete: "Delete this plan?", loading: "Loading…", off: "off",
    pick_one: "Select at least one service.",
    a_spa: "Spa", a_gym: "Gym", a_pool: "Pool", a_salon: "Salon", a_activity: "Activity",
  },
  am: {
    title: "የአገልግሎት ጥቅሎች",   // TODO verify
    intro: "የአገልግሎት ዝርዝሮችን ወደ ፕላን ያጣምሩ። ወርሃዊ ዋጋ ከአገልግሎቶቹ የይፋ ዋጋ ተደምሮ ከቅናሽ ተቀናሽ ይሰላል።",
    new: "አዲስ ፕላን", name: "የፕላን ስም", services: "የተካተቱ አገልግሎቶች",
    no_services: "እስካሁን ንቁ አገልግሎት የለም — መጀመሪያ በዝርዝሩ ውስጥ አገልግሎት ይጨምሩ።",
    discount: "የጥቅል ቅናሽ %", cycle: "የክፍያ ዑደት",
    monthly: "ወርሃዊ", quarterly: "ሩብ ዓመት", annual: "ዓመታዊ",
    active: "ገቢር", save: "ፕላን አስቀምጥ", saved: "ፕላን ተቀምጧል።",
    subtotal: "ንዑስ ድምር", price: "ወርሃዊ ዋጋ", none: "እስካሁን ፕላን የለም — ከላይ ይጨምሩ።",
    confirm_delete: "ይህን ፕላን ይሰርዙ?", loading: "በመጫን ላይ…", off: "ጠፍቷል",
    pick_one: "ቢያንስ አንድ አገልግሎት ይምረጡ።",
    a_spa: "ስፓ", a_gym: "ጂም", a_pool: "መዋኛ", a_salon: "ሳሎን", a_activity: "እንቅስቃሴ",
  },
  om: {
    title: "Tuuta tajaajilaa",   // TODO verify
    intro: "Tajaajiloota kanaalii walitti qabuun karoora tolchi. Gatiin ji'aa gatii ummataa tajaajilootaa irraa walitti ida'amee, hir'ina tuuta hir'isee shallagama.",
    new: "Karoora haaraa", name: "Maqaa karooraa", services: "Tajaajiloota dabalaman",
    no_services: "Hanga ammaa tajaajilli hojjetu hin jiru — dursaa kanaalii keessatti tajaajila dabali.",
    discount: "Hir'ina tuuta %", cycle: "Marsaa kaffaltii",
    monthly: "Ji'aan", quarterly: "Kurmaana", annual: "Waggaan",
    active: "Kan hojjetu", save: "Karoora olkaa'i", saved: "Karoorri olkaa'ame.",
    subtotal: "Ida'ama xiqqaa", price: "Gatii ji'aa", none: "Hanga ammaa karoorri hin jiru — olitti dabali.",
    confirm_delete: "Karoora kana haquu?", loading: "Fe'aa jira…", off: "cufaa",
    pick_one: "Yoo xiqqaate tajaajila tokko fili.",
    a_spa: "Spaa", a_gym: "Jiimii", a_pool: "Daakaa", a_salon: "Saaloonii", a_activity: "Sochii",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const inp = { padding: "9px 10px", border: "1px solid #d8dee2", borderRadius: 8, fontSize: 14 };

const empty = () => ({ id: null, name: "", service_ids: [], discount_pct: "", dues_cycle: "monthly", active: true });
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function MembershipPlansPage({ embedded = false }) {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (v) => formatPrice(v || 0, cur);
  const [list, setList] = useState(null);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(empty());
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => fetchMembershipPlans().then(setList).catch(() => setList([]));
  const loadServices = () => fetchServices().then((r) => setServices((r || []).filter((s) => s.active))).catch(() => setServices([]));
  useEffect(() => { load(); loadServices(); }, []);

  const toggleService = (id) => setForm((f) => ({
    ...f,
    service_ids: f.service_ids.includes(id) ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id],
  }));

  const edit = (p) => {
    setForm({ id: p.id, name: p.name || "", service_ids: (p.service_ids || []).slice(),
      discount_pct: p.discount_pct ? String(p.discount_pct) : "", dues_cycle: p.dues_cycle || "monthly",
      active: p.active !== false });
    setMsg(""); setErr("");
  };
  const reset = () => { setForm(empty()); setMsg(""); setErr(""); };

  // Live bundle math — mirrors the backend price rule so the summary matches
  // what gets stored: price = round(Σ price_public × (1 − discount/100), 2).
  const subtotal = round2(form.service_ids.reduce((sum, id) => {
    const s = services.find((x) => x.id === id);
    return sum + (s ? Number(s.price_public) || 0 : 0);
  }, 0));
  const disc = Math.max(0, Math.min(100, Number(form.discount_pct) || 0));
  const price = round2(subtotal * (1 - disc / 100));

  const save = async () => {
    if (!form.name.trim()) { setErr(`${t("name")} *`); return; }
    if (form.service_ids.length === 0) { setErr(t("pick_one")); return; }
    setErr(""); setMsg(""); setBusy(true);
    const body = {
      name: form.name.trim(),
      service_ids: form.service_ids.map((x) => Number(x)),
      discount_pct: disc,
      dues_cycle: form.dues_cycle,
      active: !!form.active,
    };
    try {
      if (form.id) await updateMembershipPlan(form.id, body); else await createMembershipPlan(body);
      setMsg(t("saved")); reset(); load();
    } catch (e) { setErr(e?.response?.data?.detail?.code || e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await deleteMembershipPlan(p.id); if (form.id === p.id) reset(); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not save."); }
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      {/* When embedded inside the merged Services page the wrapper renders the
          "Services" header + tabs, so we suppress this page's own title/intro. */}
      {!embedded ? (
        <>
          <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
          <p className="muted" style={{ marginTop: 2, maxWidth: 620 }}>{t("intro")}</p>
        </>
      ) : (
        <p className="muted" style={{ marginTop: 0, marginBottom: 8, maxWidth: 620 }}>{t("intro")}</p>
      )}
      {err && <div className="error" style={{ marginTop: 10 }}>{String(err)}</div>}

      <div style={{ ...CARD, padding: 16, marginTop: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>{form.id ? (form.name || t("title")) : t("new")}</div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
          <label style={{ fontSize: 12, color: "#5b6472" }}>{t("name")}
            <input style={{ ...inp, width: "100%", marginTop: 3 }} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label style={{ fontSize: 12, color: "#5b6472" }}>{t("discount")}
            <input style={{ ...inp, width: "100%", marginTop: 3 }} type="number" min="0" max="100" step="1"
              value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} />
          </label>
          <label style={{ fontSize: 12, color: "#5b6472" }}>{t("cycle")}
            <select style={{ ...inp, width: "100%", marginTop: 3 }} value={form.dues_cycle} onChange={(e) => setForm({ ...form, dues_cycle: e.target.value })}>
              <option value="monthly">{t("monthly")}</option>
              <option value="quarterly">{t("quarterly")}</option>
              <option value="annual">{t("annual")}</option>
            </select>
          </label>
        </div>

        <div style={{ fontSize: 12, color: "#5b6472", marginTop: 12, marginBottom: 4 }}>{t("services")}</div>
        {services.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>{t("no_services")}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 6 }}>
            {services.map((s) => (
              <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, padding: "6px 8px",
                border: "1px solid " + (form.service_ids.includes(s.id) ? "#0F6E56" : "#e6ebeb"), borderRadius: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={form.service_ids.includes(s.id)} onChange={() => toggleService(s.id)} />
                <span style={{ flex: 1 }}>{s.name} <span className="muted" style={{ fontSize: 11 }}>· {t("a_" + s.area)}</span></span>
                <span style={{ fontWeight: 600, color: "#063b48" }}>{money(s.price_public)}</span>
              </label>
            ))}
          </div>
        )}

        <div style={{ marginTop: 12, padding: "10px 12px", background: "#f6f8f8", borderRadius: 10, display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", fontSize: 13.5 }}>
          <span className="muted">{t("subtotal")}: <strong style={{ color: "#3a444d" }}>{money(subtotal)}</strong></span>
          <span className="muted">− {disc}%</span>
          <span style={{ fontWeight: 700, color: "#0F6E56" }}>{t("price")}: {money(price)}/{t(form.dues_cycle).toLowerCase()}</span>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#5b6472", marginTop: 10 }}>
          <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> {t("active")}
        </label>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
          <button className="btn primary small" disabled={busy || !form.name.trim() || form.service_ids.length === 0} onClick={save}>{t("save")}</button>
          {form.id && <button className="btn secondary small" onClick={reset}>+ {t("new")}</button>}
          {msg && <span style={{ color: "#1D9E75", fontWeight: 600, fontSize: 14 }}>{msg}</span>}
        </div>
      </div>

      {list == null ? <p className="muted" style={{ marginTop: 12 }}>{t("loading")}</p> : list.length === 0 ? (
        <p className="muted" style={{ marginTop: 12 }}>{t("none")}</p>
      ) : (
        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          {list.map((p) => (
            <div key={p.id} style={{ ...CARD, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <span style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{p.name} {p.active === false && <span className="muted" style={{ fontSize: 12 }}>({t("off")})</span>}</div>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{(p.services || []).map((s) => s.name).join(", ") || "—"}</div>
              </span>
              <span style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <div className="muted" style={{ fontSize: 11.5 }}>{money(p.subtotal)} {p.discount_pct ? `− ${p.discount_pct}%` : ""}</div>
                <div style={{ fontWeight: 700, color: "#063b48" }}>{money(p.price)}<span className="muted" style={{ fontSize: 11, fontWeight: 400 }}>/{t(p.dues_cycle).toLowerCase()}</span></div>
                <div style={{ marginTop: 6, display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <button className="btn secondary small" onClick={() => edit(p)}>✎</button>
                  <button className="btn secondary small" onClick={() => remove(p)}>✕</button>
                </div>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
